// Catalog.ID Client — HTTP API Client
// ============================================================================

import { getActiveCid } from '../state/device-storage';
import { apiBaseFor, forgetOrigin, getStoredOrigin, originAddress, rememberOrigin, type IdentityOrigin } from '../state/origins';

export class ApiError extends Error {
    constructor(
        public readonly status: number,
        public readonly body: unknown,
        /**
         * False when the server sent no parseable JSON body and `body` is a
         * client-side placeholder built from the status line. Lets callers
         * tell a controller's deliberate `{ error }` answer from a bare status.
         */
        public readonly bodyFromServer: boolean = true,
    ) {
        const serverMessage = typeof body === 'object' && body !== null && 'error' in body
            ? (body as { error?: unknown }).error
            : undefined;
        super(typeof serverMessage === 'string' && serverMessage ? serverMessage : `HTTP ${status}`);
        this.name = 'ApiError';
    }
}

/**
 * True when the server's auth layer rejected the session token itself —
 * missing, expired, revoked, or unknown — as opposed to a controller refusing
 * one specific action with 401 (wrong PIN, wrong credentials). The two need
 * opposite reactions: a dead session means "forget this token and go back to
 * the login form", a refused action means "let the user retry".
 *
 * The auth middleware stamps `code: "session_invalid"` on its 401s. A 401 with
 * no body at all is treated the same way: older servers answered that way,
 * and controllers always send a JSON `{ error }`.
 */
export function isSessionRejected(err: unknown): boolean {
    if (!(err instanceof ApiError) || err.status !== 401) return false;
    if (!err.bodyFromServer) return true;
    const body = err.body as Record<string, unknown> | null;
    return typeof body === 'object' && body !== null && body.code === 'session_invalid';
}

/** Per-request overrides. */
export interface RequestOptions {
    /**
     * Authenticate this one request with an explicit session token instead of
     * the active identity's. Leaves the active token untouched — used by the
     * logout sweep, which must speak to the server once per stored identity.
     */
    token?: string;
    /**
     * Extra request headers for this one call, merged over the auth and
     * content-type headers this client sets. Added for the account blob store's
     * compare-and-set write, whose expected generation rides in `If-Match`
     * rather than in the body (cid-encrypted-storage §4.2). Callers stay inside
     * their own header names — `Authorization` is set from the session token
     * and nothing in the client needs to replace it.
     */
    headers?: Record<string, string>;
    /**
     * The identity this request is made FOR (docs/cid-governance.md D18, M7a):
     * it goes to that identity's origin (`state/origins.ts`), and an
     * `origin_only` answer moves the identity to the origin it names and retries
     * there once. Without it, a request carrying the active session token goes to
     * the active identity's origin — with no switch on `origin_only`, because
     * such an answer may name some OTHER account's origin — and a request
     * without a token goes to the entry node.
     */
    cid?: string;
    /** An explicit server base ('' = the entry node). Wins over `cid`; an
     *  `origin_only` answer is then the caller's to handle. */
    base?: string;
    /** Send no session token even though one is active (a flow for somebody who
     *  has no account yet, run on a device where someone else is logged in). */
    anonymous?: boolean;
}

/** One identity's stored session token, and where that session lives. */
export interface StoredSession {
    cid: string;
    token: string;
    /** The identity's origin, when known; null = the entry node. */
    origin: IdentityOrigin | null;
}

const SESSION_SUFFIX = '_session';

/**
 * Every identity that currently has a session token on this device, with that
 * token. Several coexist: a personal account and each of its linked delegate
 * accounts get their own token, and the identity picker switches between them.
 * "Log Out" has to end all of them, which means one server call per token.
 */
export function listStoredSessions(): StoredSession[] {
    const sessions: StoredSession[] = [];
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key || !key.endsWith(SESSION_SUFFIX)) continue;
        const cid = key.slice(0, -SESSION_SUFFIX.length);
        if (!cid) continue;
        const token = localStorage.getItem(key);
        if (!token) continue;
        sessions.push({ cid, token, origin: getStoredOrigin(cid) });
    }
    return sessions;
}

class ApiClient {
    private sessionToken: string | null = null;

    /** Set the session token for authenticated requests, namespaced by CID. */
    setToken(token: string | null, cid?: string): void {
        this.sessionToken = token;
        const key = cid ? `${cid}_session` : null;
        if (token && key) {
            localStorage.setItem(key, token);
        }
    }

    /** Get the current session token. Restores from localStorage on first access. */
    getToken(): string | null {
        if (!this.sessionToken) {
            const cid = getActiveCid();
            if (cid) {
                this.sessionToken = localStorage.getItem(`${cid}_session`);
            }
        }
        return this.sessionToken;
    }

    /** Switch to a different identity's session token. */
    switchToCid(cid: string): boolean {
        const token = localStorage.getItem(`${cid}_session`);
        if (token) {
            this.sessionToken = token;
            return true;
        }
        return false;
    }

    /** Whether a session token exists. */
    isAuthenticated(): boolean {
        return this.getToken() !== null;
    }

    /**
     * Drop ONE identity's stored session token — the server rejected it
     * (expired, revoked, ended elsewhere). Other identities' tokens stay, since
     * the identity picker switches between them, and device enrolment is not
     * touched: it is the session that died, not the device.
     */
    forgetSession(cid: string): void {
        const key = `${cid}_session`;
        const stored = localStorage.getItem(key);
        localStorage.removeItem(key);
        // The origin goes with the session it described: the next login asks
        // again (the account may have moved — that can be why the token died).
        forgetOrigin(cid);
        // The in-memory copy is anonymous; it belongs to this identity when it
        // equals the stored value or when this identity is the active one.
        if (this.sessionToken !== null && (this.sessionToken === stored || getActiveCid() === cid)) {
            this.sessionToken = null;
        }
    }

    /** Clear all session tokens, and the origins stored with them, from localStorage. */
    clearSession(): void {
        this.sessionToken = null;
        // Clear all CID-namespaced session tokens and origins
        const toRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && (key.endsWith('_session') || key.endsWith('_origin'))) toRemove.push(key);
        }
        for (const key of toRemove) localStorage.removeItem(key);
    }

    async get<T>(path: string, opts?: RequestOptions): Promise<T> {
        return this.request<T>('GET', path, undefined, opts);
    }

    async post<T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T> {
        return this.request<T>('POST', path, body, opts);
    }

    async put<T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T> {
        return this.request<T>('PUT', path, body, opts);
    }

    async del<T = void>(path: string, body?: unknown, opts?: RequestOptions): Promise<T> {
        return this.request<T>('DELETE', path, body, opts);
    }

    private async request<T>(
        method: string, path: string, body?: unknown, opts?: RequestOptions, retried = false,
    ): Promise<T> {
        const headers: Record<string, string> = {};
        // An explicit token is a one-off: it authenticates THIS request as some
        // other identity (the multi-identity logout sweep) without disturbing
        // the active session token in memory or in storage.
        const token = opts?.anonymous ? null : (opts?.token ?? this.getToken());
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        if (body !== undefined) {
            headers['Content-Type'] = 'application/json';
        }
        if (opts?.headers) {
            for (const [name, value] of Object.entries(opts.headers)) headers[name] = value;
        }

        // One API server per identity (D18): the named identity's origin, else
        // the active identity's when its token rides along, else the entry node.
        // The entry base is '' standalone (a same-origin relative URL) and, in
        // the desktop, the entry node of the operator directory (the helper's
        // runtime-config; an origin's address is the directory's too); an origin elsewhere is an
        // absolute URL — cross-origin, which the nodes' CORS allows for
        // *.catalog.id and loopback origins.
        const identityCid = opts?.cid ?? (token && opts?.token === undefined ? getActiveCid() : null);
        const base = opts?.base ?? apiBaseFor(identityCid);
        const url = `${base}${path}`;
        const resp = await fetch(url, {
            method,
            headers,
            body: body !== undefined ? JSON.stringify(body) : undefined,
        });

        if (!resp.ok) {
            let errorBody: unknown;
            let bodyFromServer = true;
            try {
                errorBody = await resp.json();
            } catch {
                // statusText is empty under HTTP/2 — never let the message be blank.
                errorBody = { error: resp.statusText || `HTTP ${resp.status}` };
                bodyFromServer = false;
            }
            // The node says this identity lives elsewhere: remember where, and
            // ask there — once. Only for a request made explicitly FOR an
            // identity, whose refusal is about that identity. In the desktop
            // "where" is the directory's address for the named code, never the
            // node's `redirectTo` (operator-codes §7.5); a code the directory
            // does not run cid at is not followed.
            const moved = opts?.cid && opts.base === undefined && !retried
                ? originOnlyTarget(resp.status, errorBody)
                : null;
            const movedTo = moved ? await originAddress(moved.originNode, moved.redirectTo) : null;
            if (moved && movedTo !== null && movedTo !== base) {
                rememberOrigin(opts!.cid!, { code: moved.originNode, url: movedTo });
                return this.request<T>(method, path, body, opts, true);
            }
            throw new ApiError(resp.status, errorBody, bodyFromServer);
        }

        // Handle empty responses
        const text = await resp.text();
        if (!text) return undefined as T;

        return JSON.parse(text) as T;
    }
}

/** Singleton API client instance. */
export const api = new ApiClient();

/** The origin an `origin_only` refusal names, or null for any other answer. */
function originOnlyTarget(status: number, body: unknown): { originNode: string; redirectTo: string } | null {
    if (status !== 403 || typeof body !== 'object' || body === null) return null;
    const b = body as Record<string, unknown>;
    if (b.error === 'origin_only' && typeof b.redirectTo === 'string' && typeof b.originNode === 'string') {
        return { originNode: b.originNode, redirectTo: b.redirectTo.replace(/\/+$/, '') };
    }
    return null;
}

/** Check if an error is an origin_only federation error and return where the account lives. */
export function isOriginOnlyError(err: unknown): { originNode: string; redirectTo: string } | null {
    return err instanceof ApiError ? originOnlyTarget(err.status, err.body) : null;
}

/** Format an error for display, with special handling for origin_only errors. */
export function formatApiError(err: unknown, fallback: string): string {
    const origin = isOriginOnlyError(err);
    if (origin) {
        return `This operation must be performed at your home node: ${origin.redirectTo}`;
    }
    return err instanceof Error ? err.message : fallback;
}
